import {
  applyUnitCivicToStreetParts,
  splitCanadianUnitCivicAddress,
} from 'api/utils/canadianCivicAddress'
import { ParsedOsmAddress } from 'types/osmAddress'

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org'
const PHOTON_BASE = 'https://photon.komoot.io/api'
const USER_AGENT = 'PasseMoiTonBail/1.0 (https://passemoitonbail.ca)'

/** Bias geocoding toward Québec (Montréal area). */
const QUEBEC_BIAS = { lat: 46.8139, lon: -71.208 }

function normalizePlaceName(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Photon often returns "Lévis (quartier)" — city name, not a real borough. */
export function isUsableNeighborhood(
  value: string | undefined,
  city?: string
): value is string {
  const trimmed = value?.trim()
  if (!trimmed) return false
  if (/\(quartier\)/i.test(trimmed)) return false
  if (/^quartier\b/i.test(trimmed)) return false

  const withoutParens = trimmed.replace(/\s*\([^)]*\)\s*/g, '').trim()
  if (!withoutParens) return false

  if (city) {
    const cityNorm = normalizePlaceName(city)
    if (
      normalizePlaceName(trimmed) === cityNorm ||
      normalizePlaceName(withoutParens) === cityNorm
    ) {
      return false
    }
  }

  return true
}

function pickNeighborhood(
  candidates: Array<string | undefined>,
  city?: string
): string | undefined {
  for (const candidate of candidates) {
    if (isUsableNeighborhood(candidate, city)) return candidate.trim()
  }
  return undefined
}

export interface NominatimSearchResult {
  place_id: number
  lat: string
  lon: string
  display_name: string
  address?: Record<string, string>
}

interface PhotonFeature {
  geometry: { coordinates: [number, number] }
  properties: {
    housenumber?: string
    street?: string
    name?: string
    city?: string
    district?: string
    locality?: string
    state?: string
    country?: string
    postcode?: string
    type?: string
    countrycode?: string
  }
}

interface PhotonResponse {
  features?: PhotonFeature[]
}

let lastNominatimRequestAt = 0

async function rateLimitedNominatimFetch(url: string): Promise<Response> {
  const elapsed = Date.now() - lastNominatimRequestAt
  if (elapsed < 1100) {
    await new Promise(resolve => setTimeout(resolve, 1100 - elapsed))
  }
  lastNominatimRequestAt = Date.now()

  return fetch(url, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'fr-CA,fr,en',
      'User-Agent': USER_AGENT,
    },
  })
}

function firstDefined(...values: Array<string | undefined>): string | undefined {
  for (const value of values) {
    const trimmed = value?.trim()
    if (trimmed) return trimmed
  }
  return undefined
}

/** e.g. "5950", "5950A", "5950-5952" from start of user query (unit prefix already stripped). */
export function extractLeadingStreetNumber(query: string): string | undefined {
  const cleaned = splitCanadianUnitCivicAddress(query).geocodeQuery
  const match = cleaned.trim().match(/^(\d+(?:\s*[-–]\s*\d+)?[A-Za-z]?)\b/)
  return match?.[1]?.replace(/\s+/g, '')
}

function buildStreetLine(
  houseNumber: string | undefined,
  road: string,
  searchQuery: string,
  displayName?: string
): { street: string; number?: string; unit?: string } {
  const queryParts = splitCanadianUnitCivicAddress(searchQuery)
  let number = houseNumber?.trim()
  const roadName = road.trim()
  let unit = queryParts.unit

  if (!number) {
    number = queryParts.civicNumber || extractLeadingStreetNumber(queryParts.geocodeQuery)
  }

  if (!number && displayName) {
    const firstPart = displayName.split(',')[0]?.trim() ?? ''
    const displayParts = splitCanadianUnitCivicAddress(firstPart)
    if (displayParts.unit) {
      unit = unit || displayParts.unit
    }
    const splitMatch = displayParts.streetLine.match(
      /^(\d+(?:\s*[-–]\s*\d+)?[A-Za-z]?)\s+(.+)$/
    )
    if (splitMatch) {
      number = splitMatch[1].replace(/\s+/g, '')
      if (!roadName) {
        return applyUnitCivicToStreetParts({
          street: displayParts.streetLine,
          number,
          unit: unit || displayParts.unit,
        })
      }
    }
  }

  if (number && roadName) {
    const normalizedNumber = number.replace(/\s+/g, '')
    // Avoid "713-5950 Boulevard" when Photon returns unit-civic as housenumber.
    const numberParts = splitCanadianUnitCivicAddress(normalizedNumber)
    if (numberParts.unit && numberParts.civicNumber) {
      unit = unit || numberParts.unit
      number = numberParts.civicNumber
    }
    const civic = (number || normalizedNumber).replace(/\s+/g, '')
    const alreadyPrefixed =
      roadName.startsWith(`${civic} `) ||
      Boolean(splitCanadianUnitCivicAddress(roadName).civicNumber)
    const street = alreadyPrefixed
      ? splitCanadianUnitCivicAddress(roadName).streetLine
      : `${civic} ${roadName}`
    return applyUnitCivicToStreetParts({ street, number: civic, unit })
  }

  if (roadName) {
    return applyUnitCivicToStreetParts({ street: roadName, number, unit })
  }

  const fallback =
    displayName?.split(',')[0]?.trim() || queryParts.streetLine || searchQuery.trim()
  return applyUnitCivicToStreetParts({ street: fallback, number, unit })
}

function formatAddressLine(parts: Array<string | undefined>): string {
  return parts.filter(Boolean).join(', ')
}

export function parseNominatimResult(
  result: NominatimSearchResult,
  searchQuery: string
): ParsedOsmAddress {
  const addr = result.address ?? {}
  const road = addr.road || addr.pedestrian || addr.footway || addr.path || ''
  const { street, number, unit } = buildStreetLine(
    addr.house_number,
    road,
    searchQuery,
    result.display_name
  )

  const city = firstDefined(addr.city, addr.town, addr.village, addr.municipality, addr.county)
  const neighborhood = pickNeighborhood(
    [addr.suburb, addr.neighbourhood, addr.quarter, addr.city_district, addr.borough],
    city
  )

  return {
    street,
    number,
    unit,
    city,
    province: addr.state,
    country: addr.country,
    postalCode: addr.postcode?.toUpperCase(),
    neighborhood,
    formattedAddress: result.display_name,
    location: {
      lat: Number.parseFloat(result.lat),
      lng: Number.parseFloat(result.lon),
    },
  }
}

function parsePhotonFeature(feature: PhotonFeature, searchQuery: string): ParsedOsmAddress {
  const props = feature.properties
  const [lng, lat] = feature.geometry.coordinates
  const road = props.street || props.name || ''
  const { street, number, unit } = buildStreetLine(props.housenumber, road, searchQuery)

  // Prefer explicit city; locality often duplicates city and is not a borough.
  const city = firstDefined(props.city)
  const neighborhood = pickNeighborhood([props.district, props.locality], city)
  const postalCode = props.postcode?.toUpperCase()

  const formattedAddress = formatAddressLine([
    street,
    neighborhood,
    city,
    postalCode,
    props.state,
    props.country,
  ])

  return {
    street,
    number,
    unit,
    city: city || firstDefined(props.locality),
    province: props.state,
    country: props.country,
    postalCode,
    neighborhood,
    formattedAddress,
    location: { lat, lng },
  }
}

function resultScore(address: ParsedOsmAddress, searchQuery: string): number {
  let score = 0
  const queryParts = splitCanadianUnitCivicAddress(searchQuery)
  const queryNumber = queryParts.civicNumber || extractLeadingStreetNumber(queryParts.geocodeQuery)
  const queryLower = queryParts.geocodeQuery.toLowerCase()
  const queryPostal = searchQuery
    .toUpperCase()
    .replace(/\s/g, '')
    .match(/[A-Z]\d[A-Z]\d[A-Z]\d/)?.[0]

  if (address.number) score += 10
  if (queryNumber && address.number === queryNumber.replace(/\s+/g, '')) score += 20
  if (address.street.toLowerCase().includes(queryLower.replace(/^\d+\S*\s*/, ''))) score += 5
  if (address.postalCode) score += 1
  if (address.city) score += 1
  if (queryParts.unit && address.unit === queryParts.unit) score += 5

  if (isUsableNeighborhood(address.neighborhood, address.city)) score += 8

  if (queryPostal && address.postalCode?.replace(/\s/g, '').toUpperCase() === queryPostal) {
    score += 30
  }

  return score
}

/** Collapse OSM/Photon duplicates of the same civic address with different FSAs/coords. */
function normalizeCivicKey(address: ParsedOsmAddress): string {
  const normalize = (value: string) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .replace(/\b(boulevard|blvd\.?|boul\.?)\b/g, 'boul')
      .replace(/\b(avenue|ave\.?)\b/g, 'ave')
      .replace(/\b(street|rue|st\.?)\b/g, 'rue')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()

  const street = normalize(address.street || '')
  const city = normalize(address.city || '')
  if (street) return `${street}|${city}`
  return `${address.location.lat.toFixed(4)}:${address.location.lng.toFixed(4)}`
}

function mergeAddressFields(
  primary: ParsedOsmAddress,
  secondary: ParsedOsmAddress
): ParsedOsmAddress {
  const city = primary.city || secondary.city
  const neighborhood =
    pickNeighborhood([primary.neighborhood, secondary.neighborhood], city) ||
    undefined

  return {
    ...primary,
    city,
    neighborhood,
    postalCode: primary.postalCode || secondary.postalCode,
    unit: primary.unit || secondary.unit,
    number: primary.number || secondary.number,
    province: primary.province || secondary.province,
    country: primary.country || secondary.country,
  }
}

function dedupeAddresses(
  addresses: ParsedOsmAddress[],
  searchQuery: string
): ParsedOsmAddress[] {
  // Keep the best-scoring hit per civic address (street + city), not per lat/lng/postcode.
  const ranked = [...addresses].sort(
    (a, b) => resultScore(b, searchQuery) - resultScore(a, searchQuery)
  )
  const byKey = new Map<string, ParsedOsmAddress>()

  for (const item of ranked) {
    const key = normalizeCivicKey(item)
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, item)
      continue
    }
    // Prefer higher score as base, but pull a real borough from the other hit (e.g. Nominatim suburb).
    const primary =
      resultScore(existing, searchQuery) >= resultScore(item, searchQuery) ? existing : item
    const secondary = primary === existing ? item : existing
    byKey.set(key, mergeAddressFields(primary, secondary))
  }

  return Array.from(byKey.values())
}

async function searchPhotonAddresses(
  query: string,
  limit: number
): Promise<ParsedOsmAddress[]> {
  const params = new URLSearchParams({
    q: query.trim(),
    limit: String(limit),
    lang: 'fr',
    lat: String(QUEBEC_BIAS.lat),
    lon: String(QUEBEC_BIAS.lon),
  })

  const response = await fetch(`${PHOTON_BASE}/?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  })

  if (!response.ok) {
    throw new Error(`Photon search failed (${response.status})`)
  }

  const data = (await response.json()) as PhotonResponse
  if (!Array.isArray(data.features)) return []

  return data.features
    .filter(
      feature =>
        feature.geometry?.coordinates?.length === 2 &&
        (feature.properties.countrycode === 'CA' || feature.properties.country === 'Canada')
    )
    .map(feature => parsePhotonFeature(feature, query))
}

async function searchNominatimAddresses(
  query: string,
  limit: number
): Promise<ParsedOsmAddress[]> {
  const params = new URLSearchParams({
    q: query.trim(),
    format: 'json',
    addressdetails: '1',
    limit: String(limit),
    countrycodes: 'ca',
  })

  const response = await rateLimitedNominatimFetch(
    `${NOMINATIM_BASE}/search?${params.toString()}`
  )
  if (!response.ok) {
    throw new Error(`Nominatim search failed (${response.status})`)
  }

  const data = (await response.json()) as NominatimSearchResult[]
  if (!Array.isArray(data)) return []

  return data
    .filter(item => item.lat && item.lon)
    .map(item => parseNominatimResult(item, query))
}

export async function searchOsmAddresses(
  query: string,
  options?: { countryCodes?: string; limit?: number }
): Promise<ParsedOsmAddress[]> {
  const trimmed = query.trim()
  if (trimmed.length < 3) return []

  const queryParts = splitCanadianUnitCivicAddress(trimmed)
  // Geocode without the apartment prefix so OSM finds the civic building.
  const geocodeQuery = queryParts.geocodeQuery || trimmed
  const limit = options?.limit ?? 6
  const results: ParsedOsmAddress[] = []

  try {
    const photonResults = await searchPhotonAddresses(geocodeQuery, limit)
    results.push(...photonResults)
  } catch {
    // Fall back to Nominatim only below.
  }

  const needsNeighborhoodEnrichment =
    results.length === 0 ||
    results.every(item => !isUsableNeighborhood(item.neighborhood, item.city))

  if (results.length < limit || needsNeighborhoodEnrichment) {
    try {
      const nominatimResults = await searchNominatimAddresses(geocodeQuery, limit)
      results.push(...nominatimResults)
    } catch {
      if (results.length === 0) throw new Error('Address search unavailable')
    }
  }

  const withUnit = results.map(item => {
    if (item.unit || !queryParts.unit) return item
    return { ...item, unit: queryParts.unit }
  })

  return dedupeAddresses(withUnit, geocodeQuery)
    .sort((a, b) => resultScore(b, trimmed) - resultScore(a, trimmed))
    .slice(0, limit)
}

type GeocoderCaResponse = {
  latt?: string
  longt?: string
  postal?: string
  error?: unknown
  standard?: {
    stnumber?: string
    staddress?: string
    city?: string
    prov?: string
  }
}

/**
 * Refine map pin for a Canadian civic address. OSM/Photon often only know the street
 * centerline; geocoder.ca resolves house numbers more reliably for QC/CA.
 */
export async function refineCanadianCivicLocation(
  address: ParsedOsmAddress
): Promise<ParsedOsmAddress> {
  const parts = splitCanadianUnitCivicAddress(address.street)
  const streetLine = parts.streetLine || address.street
  const unit = address.unit || parts.unit
  const civicNumber = parts.civicNumber || address.number

  if (!civicNumber && !extractLeadingStreetNumber(streetLine)) {
    return {
      ...address,
      street: streetLine,
      unit,
    }
  }

  const locate = [streetLine, address.city, address.province || 'QC', address.postalCode, 'Canada']
    .filter(Boolean)
    .join(', ')

  try {
    const response = await fetch(
      `https://geocoder.ca/?locate=${encodeURIComponent(locate)}&json=1`,
      { headers: { Accept: 'application/json' } }
    )
    if (!response.ok) {
      return { ...address, street: streetLine, unit, number: civicNumber || address.number }
    }

    const data = (await response.json()) as GeocoderCaResponse
    const lat = Number.parseFloat(String(data.latt ?? ''))
    const lng = Number.parseFloat(String(data.longt ?? ''))
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || data.error) {
      return { ...address, street: streetLine, unit, number: civicNumber || address.number }
    }

    return {
      ...address,
      street: streetLine,
      number: data.standard?.stnumber || civicNumber || address.number,
      unit,
      city: address.city || data.standard?.city,
      province: address.province || data.standard?.prov,
      postalCode: data.postal?.toUpperCase() || address.postalCode,
      location: { lat, lng },
    }
  } catch {
    return { ...address, street: streetLine, unit, number: civicNumber || address.number }
  }
}

function isValidLookupResult(parsed: ParsedOsmAddress | null | undefined): parsed is ParsedOsmAddress {
  return (
    parsed != null &&
    Number.isFinite(parsed.location.lat) &&
    Number.isFinite(parsed.location.lng)
  )
}

function pickPostalLookupResult(
  results: ParsedOsmAddress[],
  compactPostalCode: string
): ParsedOsmAddress | null {
  const exact = results.find(
    item => item.postalCode?.replace(/\s/g, '').toUpperCase() === compactPostalCode
  )
  return exact ?? results[0] ?? null
}

export async function lookupCanadianPostalCode(
  postalCode: string
): Promise<ParsedOsmAddress | null> {
  const compact = postalCode.trim().replace(/\s/g, '').toUpperCase()
  if (compact.length !== 6) return null

  const formatted = `${compact.slice(0, 3)} ${compact.slice(3)}`

  try {
    const params = new URLSearchParams({
      postalcode: compact,
      format: 'json',
      addressdetails: '1',
      limit: '1',
      countrycodes: 'ca',
    })

    const response = await rateLimitedNominatimFetch(
      `${NOMINATIM_BASE}/search?${params.toString()}`
    )

    if (response.ok) {
      const data = (await response.json()) as NominatimSearchResult[]
      if (Array.isArray(data) && data.length > 0) {
        const parsed = parseNominatimResult(data[0], compact)
        if (isValidLookupResult(parsed)) return parsed
      }
    }
  } catch {
    // Fall through to alternate providers below.
  }

  try {
    const photonResults = await searchPhotonAddresses(`${formatted}, Canada`, 5)
    const photonMatch = pickPostalLookupResult(photonResults, compact)
    if (isValidLookupResult(photonMatch)) return photonMatch
  } catch {
    // Fall through to free-text Nominatim search.
  }

  try {
    const nominatimResults = await searchNominatimAddresses(`${formatted}, Canada`, 3)
    const nominatimMatch = pickPostalLookupResult(nominatimResults, compact)
    if (isValidLookupResult(nominatimMatch)) return nominatimMatch
  } catch {
    return null
  }

  return null
}

/** Detect Canadian postal code (with or without space), e.g. G1K2P8 / G1K 2P8 */
export function isCanadianPostalCodeQuery(query: string): boolean {
  return /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(query.trim())
}

function cityResultScore(address: ParsedOsmAddress, searchQuery: string): number {
  let score = 0
  const queryLower = searchQuery.trim().toLowerCase()
  const cityLower = (address.city ?? '').toLowerCase()

  if (cityLower) score += 20
  if (cityLower === queryLower) score += 40
  else if (cityLower.startsWith(queryLower)) score += 25
  else if (cityLower.includes(queryLower)) score += 10

  if (address.postalCode) score += 5
  if (address.province) score += 2
  // Prefer locality hits over street-only hits for city filter.
  if (!address.number) score += 8

  return score
}

function dedupeCities(addresses: ParsedOsmAddress[]): ParsedOsmAddress[] {
  const seen = new Set<string>()
  const unique: ParsedOsmAddress[] = []

  for (const item of addresses) {
    const cityKey = (item.city ?? item.street ?? item.formattedAddress).trim().toLowerCase()
    if (!cityKey || seen.has(cityKey)) continue
    seen.add(cityKey)
    unique.push(item)
  }

  return unique
}

/**
 * City / postal / light address search for the listings location filter.
 * Prefers municipality-level results and supports Canadian postal codes.
 */
export async function searchCitiesForFilter(
  query: string,
  options?: { limit?: number }
): Promise<ParsedOsmAddress[]> {
  const trimmed = query.trim()
  if (trimmed.length < 2) return []

  const limit = options?.limit ?? 6

  if (isCanadianPostalCodeQuery(trimmed)) {
    const postal = await lookupCanadianPostalCode(trimmed)
    return postal ? [postal] : []
  }

  const results: ParsedOsmAddress[] = []

  try {
    const photonResults = await searchPhotonAddresses(trimmed, limit * 2)
    results.push(...photonResults)
  } catch {
    // Fall back to Nominatim below.
  }

  if (results.length < limit) {
    try {
      const nominatimResults = await searchNominatimCities(trimmed, limit)
      results.push(...nominatimResults)
    } catch {
      if (results.length === 0) {
        const fallback = await searchOsmAddresses(trimmed, { countryCodes: 'ca', limit })
        results.push(...fallback)
      }
    }
  }

  return dedupeCities(results)
    .sort((a, b) => cityResultScore(b, trimmed) - cityResultScore(a, trimmed))
    .slice(0, limit)
}

async function searchNominatimCities(
  query: string,
  limit: number
): Promise<ParsedOsmAddress[]> {
  const params = new URLSearchParams({
    q: query.trim(),
    format: 'json',
    addressdetails: '1',
    limit: String(limit),
    countrycodes: 'ca',
    featuretype: 'city',
  })

  const response = await rateLimitedNominatimFetch(
    `${NOMINATIM_BASE}/search?${params.toString()}`
  )
  if (!response.ok) {
    throw new Error(`Nominatim city search failed (${response.status})`)
  }

  const data = (await response.json()) as NominatimSearchResult[]
  if (!Array.isArray(data)) return []

  return data
    .filter(item => item.lat && item.lon)
    .map(item => parseNominatimResult(item, query))
}
